import type {
  CampCategory,
  CampCategoryInsert,
  CampCategoryUpdate,
} from '#shared/types'
import type { ApiResponse, PaginationParams } from '#shared/types/api'
import { logApiError } from '~/utils/logger'
import { buildQueryString } from '~/utils/queryParams'

interface CategoriesQueryParams extends PaginationParams {
  search?: string
  isActive?: boolean
  isPublic?: boolean
}

export const useCampCategories = () => {
  /**
   * Fetch all categories with optional filters
   */
  const getCategories = async (params?: CategoriesQueryParams) => {
    try {
      const queryString = buildQueryString(params || {})
      const url = `/camps/categories${queryString ? `?${queryString}` : ''}`

      return await $api<ApiResponse<CampCategory[]>>(url, {
        method: 'GET',
      })
    }
    catch (error) {
      logApiError('/camps/categories', error, { params })
      throw error
    }
  }

  /**
   * Fetch a single category by ID
   */
  const getCategory = async (id: string) => {
    try {
      return await $api<ApiResponse<CampCategory>>(`/camps/categories/${id}`, {
        method: 'GET',
      })
    }
    catch (error) {
      logApiError(`/camps/categories/${id}`, error)
      throw error
    }
  }

  /**
   * Create a new category
   * Requires: camps:manage permission
   */
  const createCategory = async (category: CampCategoryInsert) => {
    try {
      return await $api<ApiResponse<CampCategory>>('/camps/categories', {
        method: 'POST',
        body: category,
      })
    }
    catch (error) {
      logApiError('/camps/categories', error, { campCategory: category })
      throw error
    }
  }

  /**
   * Update an existing category
   * Requires: camps:manage permission
   */
  const updateCategory = async (id: string, updates: CampCategoryUpdate) => {
    try {
      return await $api<ApiResponse<CampCategory>>(`/camps/categories/${id}`, {
        method: 'PATCH',
        body: updates,
      })
    }
    catch (error) {
      logApiError(`/camps/categories/${id}`, error, { updates })
      throw error
    }
  }

  /**
   * Delete a category
   * Requires: camps:manage permission
   * Note: Cannot delete categories that are assigned to camps
   */
  const deleteCategory = async (id: string) => {
    try {
      return await $api<ApiResponse<{ success: boolean }>>(
        `/camps/categories/${id}`,
        {
          method: 'DELETE',
        },
      )
    }
    catch (error) {
      logApiError(`/camps/categories/${id}`, error, { action: 'delete' })
      throw error
    }
  }

  /**
   * Fetch only active categories (for public use)
   */
  const getActiveCategories = async () => {
    return getCategories({
      isActive: true,
      limit: 100,
      sort: 'display_order',
      order: 'asc',
    })
  }

  return {
    getCategories,
    getCategory,
    createCategory,
    updateCategory,
    deleteCategory,
    getActiveCategories,
  }
}
